import { useState } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { ArrowUpRight } from "@phosphor-icons/react";
import { Reveal, SectionLabel, scrollToSection } from "@/components/Reveal";

const PARTNERS = [
  {
    id: "agencies",
    name: "Travel Agencies",
    note: "White-label ground handling for your Georgia programmes.",
    img: "https://images.pexels.com/photos/9223235/pexels-photo-9223235.jpeg",
  },
  {
    id: "operators",
    name: "Tour Operators",
    note: "Reliable local operations for series and ad-hoc departures.",
    img: "https://images.pexels.com/photos/37612863/pexels-photo-37612863.jpeg",
  },
  {
    id: "dmc",
    name: "Destination Management Companies",
    note: "A trusted extension of your team on the ground.",
    img: "https://images.pexels.com/photos/17426635/pexels-photo-17426635.jpeg",
  },
  {
    id: "corporate",
    name: "Corporate Travel Companies",
    note: "Business travel, MICE and delegation logistics that run on time.",
    img: "https://images.pexels.com/photos/36766695/pexels-photo-36766695.jpeg",
  },
  {
    id: "advisors",
    name: "Independent Travel Advisors",
    note: "A local partner who makes you look exceptional to your clients.",
    img: "https://images.pexels.com/photos/35699509/pexels-photo-35699509.jpeg",
  },
];

export default function PartnerTypes() {
  const [active, setActive] = useState(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 180, damping: 24 });
  const sy = useSpring(my, { stiffness: 180, damping: 24 });

  const activeImg = PARTNERS.find((p) => p.id === active)?.img;

  return (
    <section
      id="partners"
      data-testid="partners-section"
      className="relative py-24 md:py-36"
      onMouseMove={(e) => {
        mx.set(e.clientX);
        my.set(e.clientY);
      }}
    >
      <motion.div
        style={{ x: sx, y: sy, translateX: "-50%", translateY: "-110%" }}
        animate={{ opacity: active ? 1 : 0, scale: active ? 1 : 0.92 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="pointer-events-none fixed left-0 top-0 z-40 hidden h-48 w-72 overflow-hidden border border-[#184167]/40 shadow-[0_8px_32px_rgba(7,21,38,0.25)] lg:block"
        aria-hidden="true"
      >
        {activeImg && <img src={activeImg} alt="" className="h-full w-full object-cover" />}
      </motion.div>

      <div className="mx-auto max-w-[1440px] px-6 md:px-12">
        <div className="mb-16 max-w-2xl md:mb-20">
          <Reveal>
            <SectionLabel testId="partners-label">Who we work with</SectionLabel>
          </Reveal>
          <Reveal delay={0.1}>
            <h2
              data-testid="partners-heading"
              className="mt-6 font-display text-3xl font-light leading-tight tracking-tight text-[#071526] md:text-5xl"
            >
              Built for long-term <span className="italic text-[#184167]">B2B partnerships.</span>
            </h2>
          </Reveal>
        </div>

        <div data-testid="partners-list">
          {PARTNERS.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.06}>
              <a
                href="#contact"
                data-testid={`partner-row-${p.id}`}
                onClick={(e) => scrollToSection(e, "#contact")}
                onMouseEnter={() => setActive(p.id)}
                onMouseLeave={() => setActive(null)}
                className="group flex items-center justify-between gap-6 border-t border-[#E2E8F0] py-7 transition-colors duration-500 last:border-b hover:bg-[#F0F4F8] md:py-9"
              >
                <div className="flex items-baseline gap-6 md:gap-10">
                  <span className="font-display text-sm italic text-[#184167]/70">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="font-display text-2xl font-light text-[#071526] transition-colors duration-500 group-hover:text-[#184167] md:text-4xl">
                      {p.name}
                    </h3>
                    <p className="mt-2 text-sm font-light text-[#5C6B7C]">{p.note}</p>
                  </div>
                </div>
                <ArrowUpRight
                  size={26}
                  weight="thin"
                  className="shrink-0 text-[#5C6B7C] transition-[color,transform] duration-500 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-[#184167]"
                />
              </a>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
